import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Mail, Phone, CalendarDays, RefreshCw, Wallet, Wrench, ImagePlus, Trash2, Loader2, Archive, ArchiveRestore } from "lucide-react";
import { Reveal, SectionHead } from "../components/Reveal";
import Seo from "../components/Seo";
import { Logo } from "../components/Logo";
import { API, INPUT_CLS } from "../data";

const TOKEN_KEY = "edilkrea_admin_token";
const CATEGORIE = ["Ristrutturazioni", "Interni & Design", "Impiantistica", "Nuove Costruzioni"];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const fotoUrl = (f) => `${API}/foto/file/${f.storage_path}`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [codice, setCodice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("nuove");
  const [foto, setFoto] = useState([]);
  const [file, setFile] = useState(null);
  const [fTitolo, setFTitolo] = useState("");
  const [fCat, setFCat] = useState(CATEGORIE[0]);
  const [uploading, setUploading] = useState(false);

  const authHeaders = (t) => ({ headers: { Authorization: `Bearer ${t || token}` } });

  const load = async (t) => {
    try {
      const { data } = await axios.get(`${API}/preventivi`, authHeaders(t));
      setItems(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setItems(null);
    }
  };

  const loadFoto = async () => {
    try {
      const { data } = await axios.get(`${API}/foto`);
      setFoto(data);
    } catch {}
  };

  useEffect(() => {
    if (token) {
      load();
      loadFoto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { codice });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Accesso effettuato", { description: "Benvenuto nell'area riservata." });
      load(data.token);
      loadFoto();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Errore di accesso. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setItems(null);
    toast.success("Disconnesso");
  };

  const archivia = async (p, flag) => {
    try {
      await axios.patch(`${API}/preventivi/${p.id}`, { archiviato: flag }, authHeaders());
      setItems(items.map((it) => (it.id === p.id ? { ...it, archiviato: flag } : it)));
      toast.success(flag ? "Richiesta archiviata" : "Richiesta ripristinata", {
        description: flag ? "La trovi nella scheda Archiviate." : "La richiesta è tornata tra le nuove.",
      });
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const elimina = async (p) => {
    if (!window.confirm(`Eliminare definitivamente la richiesta di ${p.nome} ${p.cognome}?`)) return;
    try {
      await axios.delete(`${API}/preventivi/${p.id}`, authHeaders());
      setItems(items.filter((it) => it.id !== p.id));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const uploadFoto = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Seleziona una foto prima di caricare");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("titolo", fTitolo);
      fd.append("categoria", fCat);
      await axios.post(`${API}/foto`, fd, authHeaders());
      toast.success("Foto pubblicata!", { description: "Ora è visibile nella Galleria Lavori." });
      setFile(null);
      setFTitolo("");
      loadFoto();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error("Upload fallito", { description: typeof d === "string" ? d : "Riprova con un'altra foto." });
    } finally {
      setUploading(false);
    }
  };

  const deleteFoto = async (id) => {
    try {
      await axios.delete(`${API}/foto/${id}`, authHeaders());
      setFoto(foto.filter((f) => f.id !== id));
      toast.success("Foto rimossa dalla galleria");
    } catch {
      toast.error("Errore durante la rimozione");
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="pt-20 min-h-screen flex items-center justify-center px-5 py-16">
        <Seo title="Area Riservata | Edilkrea Technoimpianti" description="Area riservata Edilkrea Technoimpianti." noindex />
        <Reveal className="w-full max-w-md">
          <form onSubmit={login} className="rounded-2xl border border-gold/20 bg-card2 p-8" data-testid="admin-login-form">
            <div className="flex justify-center"><Logo className="h-14 w-14" /></div>
            <h1 className="mt-5 text-center font-display text-2xl font-bold uppercase text-cream">Area Riservata</h1>
            <p className="mt-2 text-center text-sm text-slate-400">Accesso riservato a Edilkrea Technoimpianti</p>
            <div className="mt-8 space-y-4">
              <input
                data-testid="admin-code-input"
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                required
                autoComplete="off"
                placeholder="Codice di accesso"
                value={codice}
                onChange={(e) => setCodice(e.target.value.replace(/\D/g, ""))}
                className={`${INPUT_CLS} text-center text-2xl tracking-[0.5em] font-mono placeholder:tracking-normal placeholder:text-sm`}
              />
              {error && (
                <p data-testid="admin-login-error" className="rounded-lg border border-copper/40 bg-copper/10 px-4 py-3 text-sm text-copper">
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="admin-login-submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper disabled:opacity-60"
              >
                <Lock className="h-4 w-4" />
                {loading ? "Accesso in corso..." : "Accedi"}
              </button>
            </div>
          </form>
        </Reveal>
      </main>
    );
  }

  const nuove = (items || []).filter((p) => !p.archiviato);
  const archiviate = (items || []).filter((p) => p.archiviato);
  const shown = tab === "nuove" ? nuove : archiviate;

  return (
    <main data-testid="admin-dashboard" className="pt-20 min-h-screen">
      <Seo title="Area Riservata | Edilkrea Technoimpianti" description="Area riservata Edilkrea Technoimpianti." noindex />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-14 sm:pt-20 pb-24">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead overline="Area Riservata" title="Richieste di Preventivo" />
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={() => { load(); loadFoto(); }}
              className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              <RefreshCw className="h-4 w-4" /> Aggiorna
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-copper/40 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-copper transition-colors hover:bg-copper hover:text-ink"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>

        <div className="mt-6 flex gap-2" data-testid="admin-tabs">
          <button
            data-testid="tab-nuove"
            onClick={() => setTab("nuove")}
            className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all ${
              tab === "nuove" ? "bg-gold text-ink" : "border border-gold/20 text-cream/70 hover:border-gold/50 hover:text-gold"
            }`}
          >
            Nuove ({nuove.length})
          </button>
          <button
            data-testid="tab-archiviate"
            onClick={() => setTab("archiviate")}
            className={`rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all ${
              tab === "archiviate" ? "bg-gold text-ink" : "border border-gold/20 text-cream/70 hover:border-gold/50 hover:text-gold"
            }`}
          >
            Archiviate ({archiviate.length})
          </button>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {shown.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 0.08}>
              <article className={`h-full rounded-2xl border bg-card2 p-6 transition-colors ${p.archiviato ? "border-gold/10 opacity-80" : "border-gold/15 hover:border-gold/40"}`} data-testid={`preventivo-card-${i}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 className="font-display text-xl font-bold text-cream">{p.nome} {p.cognome}</h3>
                  <span className="rounded-full bg-copper/15 border border-copper/30 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-copper">
                    {p.servizio}
                  </span>
                </div>
                <div className="mt-4 grid gap-2 text-sm text-slate-300">
                  <a href={`tel:${p.telefono}`} className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                    <Phone className="h-4 w-4 text-gold" /> {p.telefono}
                  </a>
                  <a href={`mailto:${p.email}`} className="inline-flex items-center gap-2 hover:text-gold transition-colors">
                    <Mail className="h-4 w-4 text-gold" /> {p.email}
                  </a>
                  {p.budget && (
                    <span className="inline-flex items-center gap-2">
                      <Wallet className="h-4 w-4 text-gold" /> Budget: {p.budget}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2 text-slate-500">
                    <CalendarDays className="h-4 w-4" /> {formatDate(p.created_at)}
                  </span>
                </div>
                <p className="mt-4 rounded-xl bg-ink/60 border border-gold/10 p-4 text-sm leading-relaxed text-slate-300">
                  <Wrench className="mb-2 h-4 w-4 text-copper" />
                  {p.messaggio}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {p.archiviato ? (
                    <button
                      data-testid={`preventivo-restore-${i}`}
                      onClick={() => archivia(p, false)}
                      className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-ink"
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" /> Ripristina
                    </button>
                  ) : (
                    <button
                      data-testid={`preventivo-archive-${i}`}
                      onClick={() => archivia(p, true)}
                      className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-ink"
                    >
                      <Archive className="h-3.5 w-3.5" /> Archivia
                    </button>
                  )}
                  <button
                    data-testid={`preventivo-delete-${i}`}
                    onClick={() => elimina(p)}
                    className="inline-flex items-center gap-2 rounded-full border border-copper/40 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-copper transition-colors hover:bg-copper hover:text-ink"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Elimina
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        {items !== null && shown.length === 0 && (
          <div className="mt-16 rounded-2xl border border-dashed border-gold/20 p-16 text-center" data-testid="admin-empty-state">
            <p className="font-display text-xl font-bold uppercase text-cream">
              {tab === "nuove" ? "Nessuna richiesta nuova" : "Nessuna richiesta archiviata"}
            </p>
            <p className="mt-2 text-sm text-slate-400">
              {tab === "nuove"
                ? "Le richieste inviate dal modulo \"Richiedi un Preventivo\" appariranno qui."
                : "Le richieste che archivi dopo averle lavorate appariranno qui."}
            </p>
          </div>
        )}

        <div className="mt-24 border-t border-gold/10 pt-16">
          <SectionHead overline="Vetrina Lavori" title="Gestisci le Foto" />
          <p className="mt-4 max-w-2xl text-sm text-slate-400">
            Le foto caricate qui vengono pubblicate automaticamente nella pagina Galleria Lavori, nella categoria scelta.
          </p>

          <Reveal className="mt-8">
            <form onSubmit={uploadFoto} className="rounded-2xl border border-gold/15 bg-card2 p-6 sm:p-8" data-testid="foto-upload-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="foto-titolo-input"
                  required
                  placeholder="Titolo del lavoro (es. Parete TV in marmo)"
                  value={fTitolo}
                  onChange={(e) => setFTitolo(e.target.value)}
                  className={INPUT_CLS}
                />
                <select
                  data-testid="foto-categoria-select"
                  value={fCat}
                  onChange={(e) => setFCat(e.target.value)}
                  className={INPUT_CLS}
                >
                  {CATEGORIE.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <label
                  htmlFor="foto-file-input"
                  className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-gold/30 px-6 py-3 text-sm font-semibold uppercase tracking-wider text-gold transition-colors hover:bg-gold hover:text-ink"
                >
                  <ImagePlus className="h-4 w-4" />
                  {file ? file.name : "Scegli Foto"}
                </label>
                <input
                  id="foto-file-input"
                  data-testid="foto-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <button
                  type="submit"
                  data-testid="foto-upload-submit"
                  disabled={uploading}
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold uppercase tracking-wider text-ink transition-all hover:bg-copper disabled:opacity-60"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                  {uploading ? "Caricamento..." : "Pubblica in Galleria"}
                </button>
              </div>
            </form>
          </Reveal>

          <p className="mt-10 font-mono text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="foto-count">
            {foto.length} foto pubblicate
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-foto-grid">
            {foto.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 0.06}>
                <figure className="group relative overflow-hidden rounded-2xl border border-gold/15" data-testid={`foto-card-${i}`}>
                  <img src={fotoUrl(f)} alt={f.titolo} className="h-56 w-full object-cover" loading="lazy" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink to-transparent p-4 pt-10">
                    <p className="font-display text-sm font-bold uppercase text-cream">{f.titolo}</p>
                    <p className="font-mono text-[11px] uppercase tracking-widest text-copper">{f.categoria}</p>
                  </figcaption>
                  <button
                    data-testid={`foto-delete-${i}`}
                    onClick={() => deleteFoto(f.id)}
                    className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-ink/70 border border-copper/40 text-copper opacity-100 sm:opacity-0 transition-opacity group-hover:opacity-100 hover:bg-copper hover:text-ink"
                    aria-label={`Elimina ${f.titolo}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
